import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { buildSubdomain } from '@/lib/domain';

/**
 * Visibilité du site internet du commerce.
 * Désactivé → les visiteurs sont renvoyés vers la page de connexion.
 */
export default function SiteVisibilitySettings() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from('users')
          .select('site_enabled')
          .eq('id', user.id)
          .maybeSingle();
        if (err) throw err;
        if (!cancelled) setEnabled(data?.site_enabled !== false);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Impossible de charger la visibilité du site.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const persist = async (next: boolean) => {
    if (!user) return;
    setSaving(true);
    setError('');
    try {
      const { error: err } = await supabase
        .from('users')
        .update({ site_enabled: next })
        .eq('id', user.id);
      if (err) throw err;
      setEnabled(next);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible d'enregistrer ce réglage.");
    } finally {
      setSaving(false);
    }
  };

  const siteUrl = user?.user_name ? buildSubdomain(user.user_name) : '';

  if (loading) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 flex items-center gap-2 text-sm text-foreground-400">
        <i className="ri-loader-4-line animate-spin"></i>Chargement…
      </div>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
      <h3 className="text-sm font-semibold text-foreground-700 mb-1">Visibilité de mon site internet</h3>
      <p className="text-xs text-foreground-500 mb-5">
        Choisissez si votre vitrine est accessible au public ou réservée à la connexion.
      </p>

      <button
        type="button"
        onClick={() => void persist(!enabled)}
        disabled={saving}
        className="w-full flex items-center justify-between gap-4 p-4 rounded-lg border border-background-200/70 bg-background-100/60 cursor-pointer disabled:opacity-60"
      >
        <span className="flex items-start gap-3 text-left">
          <span className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-500'}`}>
            <i className={enabled ? 'ri-global-line' : 'ri-eye-off-line'}></i>
          </span>
          <span>
            <span className="block text-sm font-semibold text-foreground-900">
              {enabled ? 'Site internet activé' : 'Site internet désactivé'}
            </span>
            <span className="block text-xs text-foreground-500 mt-0.5">
              {enabled
                ? 'Vos visiteurs voient votre catalogue et peuvent commander.'
                : 'Les visiteurs sont renvoyés directement vers la page de connexion.'}
            </span>
          </span>
        </span>
        <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-accent-500' : 'bg-background-300'}`}>
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </span>
      </button>

      {saving && (
        <p className="mt-3 text-xs text-foreground-500 flex items-center gap-1.5">
          <i className="ri-loader-4-line animate-spin"></i>Enregistrement…
        </p>
      )}
      {saved && !saving && (
        <p className="mt-3 text-xs text-accent-700 flex items-center gap-1.5">
          <i className="ri-checkbox-circle-line"></i>Réglage enregistré.
        </p>
      )}
      {error && (
        <p className="mt-3 text-xs text-red-600 flex items-center gap-1.5">
          <i className="ri-error-warning-line"></i>{error}
        </p>
      )}

      {!enabled && (
        <div className="mt-4 p-3 rounded-lg bg-secondary-50 border border-secondary-200/70">
          <p className="text-xs text-secondary-900 leading-relaxed">
            <i className="ri-information-line mr-1"></i>
            Votre site n&apos;est plus public. Vous pouvez le réactiver à tout moment avec cet interrupteur.
          </p>
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-background-200/70 flex flex-wrap items-center gap-3">
        <a
          href={siteUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold no-underline whitespace-nowrap transition-colors ${
            siteUrl ? 'bg-background-100 text-foreground-700 hover:bg-background-200/70' : 'bg-background-100 text-foreground-300 pointer-events-none'
          }`}
        >
          <i className="ri-external-link-line"></i>
          Voir mon site
        </a>
        <span className="text-[11px] text-foreground-400 truncate">
          {siteUrl ? siteUrl.replace('https://', '') : 'Aucun sous-domaine'}
        </span>
      </div>
    </div>
  );
}